import api from "../../../services/apiClient";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import {
  extractSuccessStory,
  firstSuccessStoryRow,
  isProcFailure,
} from "../../../services/dmsMasterService";
import { fetchOrgMessagingSettings } from "../../../services/superAdminOrgService";
import utils from "../../../utils/utils";
import { guidOrNull } from "../../../utils/guidUtils";
import { getVisitApiErrorMessage } from "./scheduleService";

export const ORG_SMS_UNAVAILABLE_REASON =
  "SMS is not enabled for this organisation. This channel stays off until the AQ team turns it on. Please contact the AQ team.";

export const ORG_WHATSAPP_UNAVAILABLE_REASON =
  "WhatsApp is not enabled for this organisation. This channel stays off until the AQ team turns it on. Please contact the AQ team.";

export async function fetchOrganisationChannelFlags(organizationId) {
  if (!organizationId) {
    return { smsEnabled: false, whatsAppEnabled: false };
  }
  try {
    const messaging = await fetchOrgMessagingSettings(organizationId);
    return {
      smsEnabled: !!messaging.smsEnabled,
      whatsAppEnabled: !!messaging.whatsAppEnabled,
    };
  } catch {
    return { smsEnabled: false, whatsAppEnabled: false };
  }
}

export function applyOrgChannelAvailability(
  templates,
  { smsEnabled = false, whatsAppEnabled = false } = {},
) {
  return (templates || []).map((template) => ({
    ...template,
    IsSMS: smsEnabled ? !!template.IsSMS : false,
    IsWhatsApp: whatsAppEnabled ? !!template.IsWhatsApp : false,
  }));
}

function mapBeforeTemplateForAudit(beforeTemplate, activeTemplateType) {
  if (!beforeTemplate) return null;
  return {
    TemplateType: beforeTemplate.TemplateType || activeTemplateType || "",
    IsEmail: !!beforeTemplate.IsEmail,
    IsSMS: !!beforeTemplate.IsSMS,
    IsWhatsApp: !!beforeTemplate.IsWhatsApp,
    EmailSubject: beforeTemplate.EmailSubject || "",
    EmailText: beforeTemplate.EmailText || "",
    SMSText: beforeTemplate.SMSText || "",
    WhatsAppText: beforeTemplate.WhatsAppText || "",
    IsAdhoc: !!beforeTemplate.IsAdhoc,
  };
}

/** SQL uniqueidentifier / decimal params — never send "" (causes conversion errors). */
function optionalDecimalField(value) {
  const text = String(value ?? "").trim();
  return text === "" ? undefined : text;
}

/** Must match legacy EComm TemplateType values (NewScheduleBasicSetup.js). */
export const COMM_TEMPLATE_TYPES = [
  "Booking",
  "Reschedule",
  "Cancellation",
  "Reminder",
  "LocationChange",
];

export const COMM_TEMPLATE_LABELS = {
  Booking: "Booking",
  Reschedule: "Reschedule",
  Cancellation: "Cancel",
  Reminder: "Reminder",
  LocationChange: "Change Location",
};

export function emptyCommTemplates() {
  return COMM_TEMPLATE_TYPES.map((templateType) => ({
    TemplateType: templateType,
    SMSText: "",
    WhatsAppText: "",
    EmailSubject: "",
    EmailText: "",
    IsSMS: false,
    IsEmail: true,
    IsWhatsApp: false,
    IsAdhoc: false,
  }));
}

function normalizeCommTemplateRow(row) {
  return {
    TemplateType: row.TemplateType || row.templateType,
    SMSText: row.SMSText ?? row.smsText ?? "",
    WhatsAppText: row.WhatsAppText ?? row.whatsAppText ?? "",
    EmailSubject: row.EmailSubject ?? row.emailSubject ?? "",
    EmailText: row.EmailText ?? row.emailText ?? "",
    IsSMS: !!(row.IsSMS ?? row.isSMS),
    IsEmail: !!(row.IsEmail ?? row.isEmail),
    IsWhatsApp: !!(row.IsWhatsApp ?? row.isWhatsApp),
    IsAdhoc: !!(row.IsAdhoc ?? row.isAdhoc),
  };
}

function rowLooksLikeCommTemplate(row) {
  return !!(row && (row.TemplateType != null || row.templateType != null));
}

function rowsFromUnknownTables(dataset) {
  if (!dataset || typeof dataset !== "object") return [];
  const candidates = [];
  if (Array.isArray(dataset.Tables)) {
    dataset.Tables.forEach((table) => {
      const rows = Array.isArray(table?.Rows) ? table.Rows : Array.isArray(table) ? table : [];
      if (rows.some(rowLooksLikeCommTemplate)) candidates.push(rows);
    });
  }
  Object.keys(dataset).forEach((key) => {
    if (/success|whatsapp|payment|currency|department/i.test(key)) return;
    const value = dataset[key];
    const rows = Array.isArray(value) ? value : Array.isArray(value?.Rows) ? value.Rows : [];
    if (rows.some(rowLooksLikeCommTemplate)) candidates.push(rows);
  });
  return candidates[0] || [];
}

function rowsFromResponse(response, tableNames = [], tableIndex = -1) {
  const dataset = response?.data ?? response?.Data;
  if (!dataset || typeof dataset !== "object") return [];

  for (const name of tableNames) {
    const direct = dataset[name];
    if (Array.isArray(direct)) return direct;
    if (direct?.Rows && Array.isArray(direct.Rows)) return direct.Rows;
  }

  if (Array.isArray(dataset.Tables)) {
    for (const name of tableNames) {
      const table = dataset.Tables.find(
        (t) =>
          String(t?.TableName || t?.Name || t?.tableName || "").toLowerCase() ===
          name.toLowerCase(),
      );
      if (Array.isArray(table?.Rows)) return table.Rows;
    }
    if (tableIndex >= 0 && Array.isArray(dataset.Tables[tableIndex]?.Rows)) {
      return dataset.Tables[tableIndex].Rows;
    }
  }

  const positionalKeys = [
    "Table",
    "Table1",
    "Table2",
    "Table3",
    "Table4",
    "Table5",
    "Table6",
  ];
  if (tableIndex >= 0 && tableIndex < positionalKeys.length) {
    const key = positionalKeys[tableIndex];
    const alt = dataset[key] ?? dataset[key?.toLowerCase?.()];
    if (Array.isArray(alt)) return alt;
    if (alt?.Rows && Array.isArray(alt.Rows)) return alt.Rows;
  }

  const keys = Object.keys(dataset);
  for (const name of tableNames) {
    const ci = keys.find((k) => k.toLowerCase() === name.toLowerCase());
    if (ci && Array.isArray(dataset[ci])) return dataset[ci];
    if (ci && dataset[ci]?.Rows && Array.isArray(dataset[ci].Rows)) {
      return dataset[ci].Rows;
    }
  }

  return [];
}

export function mergePlannerRows(primary, fallback) {
  if (!primary && !fallback) return null;
  const merged = { ...(fallback || {}) };
  if (primary && typeof primary === "object") {
    Object.entries(primary).forEach(([key, value]) => {
      if (
        value !== undefined &&
        value !== null &&
        value !== "" &&
        Number(value) !== 0
      ) {
        merged[key] = value;
      }
    });
  }
  return Object.keys(merged).length ? merged : null;
}

export function buildPlannerSnapshotFromApiRow(row) {
  if (!row || typeof row !== "object") return null;
  const slotDuration = row.SlotDuration ?? row.slotDuration;
  const booking =
    row.NoOfbookingPerSlot ??
    row.noOfbookingPerSlot ??
    row.NoOfBookingPerSlot ??
    row.noOfBookingPerSlot;
  const payment =
    row.VisitPayment ?? row.visitPayment ?? row.Payment ?? row.payment;
  const visitCost = row.VisitCost ?? row.visitCost;
  const randomCost = row.RandomCost ?? row.randomCost;
  const hasAny = [slotDuration, booking, payment, visitCost, randomCost].some(
    (value) => value !== undefined && value !== null && value !== "" && Number(value) !== 0,
  );
  if (!hasAny) return null;
  return {
    SlotDuration: slotDuration,
    NoOfbookingPerSlot: booking,
    VisitPayment: payment,
    VisitCost: visitCost,
    RandomCost: randomCost,
  };
}

function datasetObject(response) {
  const dataset = response?.data ?? response?.Data;
  if (!dataset || typeof dataset !== "object") return {};
  return dataset;
}

export async function fetchVisitPlannerDetails({
  studyScheduleId,
  visitId,
  organizationId,
  createdBy,
  plannerFallback = null,
}) {
  const response = await api.post(API_ENDPOINTS.ESCHEDULE.INDIVIDUAL_VISIT, {
    StudyId: studyScheduleId,
    VisitId: visitId,
    OrganizationId: organizationId,
    CreatedBy: createdBy,
    Action: "GetVisitDetails",
  });
  if (!utils.isApiSuccess(response)) {
    throw new Error(
      utils.getApiResponseMessage(response, "Could not load visit setup."),
    );
  }
  const planner = mergePlannerRows(
    rowsFromResponse(
      response,
      ["VisitWisePlannerDetails", "visitWisePlannerDetails"],
      3,
    )[0] || null,
    plannerFallback,
  );
  const departments = rowsFromResponse(
    response,
    ["DepartmentRoleList", "departmentRoleList"],
    4,
  );
  const currency =
    rowsFromResponse(response, ["CurrencySymbol", "currencySymbol"], 6)[0] ||
    rowsFromResponse(response, ["CurrencySymbol", "currencySymbol"], 5)[0] ||
    null;
  return { planner, departments, currencySymbol: currency?.CurrencySymbol || "£" };
}

export const VISIT_SLOT_DURATION_OPTIONS = [
  { value: "5", label: "5 min" },
  { value: "15", label: "15 min" },
  { value: "30", label: "30 min" },
  { value: "60", label: "60 min" },
  { value: "90", label: "90 min" },
  { value: "120", label: "120 min" },
  { value: "150", label: "150 min" },
  { value: "180", label: "180 min" },
  { value: "210", label: "210 min" },
  { value: "240", label: "240 min" },
];

export function readPlannerSlotFields(planner) {
  const row = planner || {};
  const booking =
    row.NoOfbookingPerSlot ??
    row.NoOfBookingPerSlot ??
    row.noOfbookingPerSlot ??
    row.noOfBookingPerSlot ??
    "";
  const duration = row.SlotDuration ?? row.slotDuration ?? "";
  return {
    bookingPerSlot: booking === 0 || booking === "0" ? "" : String(booking || ""),
    slotDuration: duration === 0 || duration === "0" ? "" : String(duration || ""),
    hasSaved:
      Number(booking) > 0 && Number(duration) > 0,
  };
}

function formatPlannerAmount(value) {
  if (value === 0 || value === "0" || value === null || value === undefined) {
    return "";
  }
  return String(value);
}

export function readPlannerFinancialFields(planner) {
  const row = planner || {};
  const payment =
    row.VisitPayment ??
    row.visitPayment ??
    row.Payment ??
    row.payment ??
    "";
  const visitCost = row.VisitCost ?? row.visitCost ?? "";
  const randomCost = row.RandomCost ?? row.randomCost ?? "";
  return {
    payment: formatPlannerAmount(payment),
    visitCost: formatPlannerAmount(visitCost),
    randomCost: formatPlannerAmount(randomCost),
    hasSavedPayment: Number(payment) > 0,
    hasSavedVisitCost: Number(visitCost) > 0,
    hasSavedRandomCost: Number(randomCost) > 0,
  };
}

export async function saveVisitPlannerSetup({
  studyScheduleId,
  visitId,
  organizationId,
  createdBy,
  bookingPerSlot,
  slotDuration,
}) {
  const bookings = Number(bookingPerSlot);
  const duration = Number(slotDuration);
  if (!Number.isFinite(bookings) || bookings <= 0) {
    throw new Error("Booking per slot must be greater than zero.");
  }
  if (!Number.isFinite(duration) || duration <= 0) {
    throw new Error("Select a slot duration.");
  }
  const response = await api.post(API_ENDPOINTS.EPLANNER.SET_VISIT_PLANNER, {
    StudyId: studyScheduleId,
    VisitId: visitId,
    OrganizationId: organizationId,
    CreatedBy: createdBy,
    NoOfBookingPerSlot: bookings,
    NoOfbookingPerSlot: bookings,
    SlotDuration: duration,
    IsDayTimeReq: 0,
    DayStartTime: "",
    DayEndTime: "",
    Actiontype: "SetVisitPlanner",
  });
  if (!utils.isApiSuccess(response)) {
    throw new Error(
      utils.getApiResponseMessage(response, "Could not save planner setup."),
    );
  }
  return response;
}

export async function fetchEcommTemplates({
  studyScheduleId,
  visitId,
  organizationId,
  createdBy,
  groupId = null,
}) {
  const response = await api.post(API_ENDPOINTS.ESCHEDULE.SAVE_ECOMM_SETUP, {
    StudyId: studyScheduleId,
    VisitId: visitId,
    OrganizationId: organizationId,
    CreatedBy: createdBy,
    ActionType: "GetTemplate",
    GroupId: guidOrNull(groupId),
  });
  if (!utils.isApiSuccess(response)) {
    throw new Error(
      utils.getApiResponseMessage(response, "Could not load communication templates."),
    );
  }
  const ds = datasetObject(response);
  const templates = rowsFromResponse(response, ["TemplateList", "templateList"]);
  const list = templates.length
    ? templates
    : Array.isArray(ds.TemplateList)
      ? ds.TemplateList
      : rowsFromUnknownTables(ds);
  const whatsAppRows = rowsFromResponse(response, [
    "WhatsAppTemplates",
    "whatsAppTemplates",
  ]);
  const departments = rowsFromResponse(response, [
    "DepartmentRoleList",
    "departmentRoleList",
  ]);
  const paymentRow = rowsFromResponse(response, [
    "PaymentDetails",
    "paymentDetails",
  ])[0];
  const currency = rowsFromResponse(response, [
    "CurrencySymbol",
    "currencySymbol",
  ])[0];
  return {
    templates: mergeCommTemplates(list, whatsAppRows),
    departments,
    paymentDetails: paymentRow,
    currencySymbol: currency?.CurrencySymbol || "£",
  };
}

function platformWhatsAppText(whatsAppRows = [], templateType) {
  const match = (whatsAppRows || []).find((row) => {
    const key = row.TemplateKey || row.templateKey || row.TemplateType || row.templateType;
    return String(key || "") === String(templateType || "");
  });
  return {
    text: match?.BodyText ?? match?.bodyText ?? match?.WhatsAppText ?? match?.whatsAppText ?? "",
    notes: match?.VariableNotes ?? match?.variableNotes ?? "",
  };
}

export function mergeCommTemplates(apiRows = [], whatsAppRows = []) {
  const byType = {};
  (apiRows || []).forEach((row) => {
    const type = row.TemplateType || row.templateType;
    if (!type) return;
    const next = normalizeCommTemplateRow(row);
    const prev = byType[type];
    if (!prev || getSendableCommChannels(next).length > getSendableCommChannels(prev).length) {
      byType[type] = next;
    }
  });
  return COMM_TEMPLATE_TYPES.map((type) => {
    const base =
      byType[type] || emptyCommTemplates().find((t) => t.TemplateType === type);
    const platform = platformWhatsAppText(whatsAppRows, type);
    return {
      ...base,
      WhatsAppText: platform.text || base.WhatsAppText || "",
      WhatsAppNotes: platform.notes,
    };
  });
}

function stripCommEmailHtml(html) {
  const raw = String(html || "").trim();
  if (!raw || raw === "<p><br></p>" || raw === "<p></p>") return "";
  return raw.replace(/<[^>]+>/g, "").replace(/&nbsp;/gi, " ").trim();
}

function hasCommEmailBodyContent(template) {
  return !!stripCommEmailHtml(template?.EmailText);
}

function hasCommSmsContent(template) {
  return !!String(template?.SMSText || "").trim();
}

function hasCommWhatsAppContent(template) {
  return !!String(template?.WhatsAppText || "").trim();
}

/** Same send rule as API / reminder: channel on + template filled. */
export function getSendableCommChannels(template) {
  const channels = [];
  if (!template) return channels;
  if (
    template.IsEmail &&
    !!String(template.EmailSubject || "").trim() &&
    hasCommEmailBodyContent(template)
  ) {
    channels.push("Email");
  }
  if (template.IsSMS && hasCommSmsContent(template)) {
    channels.push("SMS");
  }
  if (template.IsWhatsApp && hasCommWhatsAppContent(template)) {
    channels.push("WhatsApp");
  }
  return channels;
}

/** Save only types that would actually send: channel on + required fields filled. */
export function commTemplateHasSaveableContent(template) {
  return getSendableCommChannels(template).length > 0;
}

/** Active-tab checks from legacy NewScheduleBasicSetup.js (enabled channel → required fields). */
export function validateActiveCommTemplate(template) {
  const messages = [];
  if (!template) {
    return { valid: false, messages: ["There is no template filled."] };
  }
  if (template.IsSMS && !hasCommSmsContent(template)) {
    messages.push("Please fill the SMS text.");
  }
  if (template.IsWhatsApp && !hasCommWhatsAppContent(template)) {
    messages.push("Please fill the WhatsApp text.");
  }
  if (template.IsEmail) {
    if (!String(template.EmailSubject || "").trim()) {
      messages.push("Please fill the Email Subject.");
    }
    if (!hasCommEmailBodyContent(template)) {
      messages.push("Please fill the Email body.");
    }
  }
  return { valid: messages.length === 0, messages };
}

/** Readiness: only enabled SMS/email channels count; both off = nothing required for that template. */
export function isCommTemplateReadyForPreview(template) {
  if (!template) return false;
  if (!template.IsSMS && !template.IsEmail && !template.IsWhatsApp) return true;
  return validateActiveCommTemplate(template).valid;
}

export function getIncompleteCommTemplateLabels(templates = []) {
  const list = templates || [];
  return COMM_TEMPLATE_TYPES.filter((type) => {
    const template = list.find((row) => row.TemplateType === type);
    return !isCommTemplateReadyForPreview(template);
  }).map((type) => COMM_TEMPLATE_LABELS[type] || type);
}

/** Go Live: every appointment type must be ready (enabled channels filled, or all channels off). */
export function hasCommTemplatesForPreview(templates = []) {
  const list = templates || [];
  if (!list.length) return false;
  return getIncompleteCommTemplateLabels(list).length === 0;
}

export function validateCommTemplatesForSave({
  templates,
  activeTemplateType,
  orgSmsEnabled = true,
  orgWhatsAppEnabled = true,
}) {
  const gated = applyOrgChannelAvailability(templates, {
    smsEnabled: orgSmsEnabled,
    whatsAppEnabled: orgWhatsAppEnabled,
  });
  const active =
    gated.find((t) => t.TemplateType === activeTemplateType) || gated[0];
  const activeResult = validateActiveCommTemplate(active);
  if (!activeResult.valid) {
    throw new Error(activeResult.messages.join(" "));
  }

  const filled = gated.filter(commTemplateHasSaveableContent);
  if (
    active &&
    !filled.some((template) => template.TemplateType === active.TemplateType)
  ) {
    filled.unshift(active);
  }
  if (!filled.length) {
    throw new Error("There is no template filled.");
  }
  return filled;
}

export async function saveEcommTemplates({
  studyScheduleId,
  trialStudyId = null,
  visitId,
  organizationId,
  createdBy,
  templates,
  activeTemplateType,
  groupId = null,
  visitName = "",
  beforeTemplate = null,
  isTemplateUpdate = false,
  onAuditWritten = null,
  orgSmsEnabled = true,
  orgWhatsAppEnabled = true,
}) {
  const filled = validateCommTemplatesForSave({
    templates,
    activeTemplateType,
    orgSmsEnabled,
    orgWhatsAppEnabled,
  });
  const response = await api.post(API_ENDPOINTS.ESCHEDULE.SAVE_ECOMM_SETUP, {
    StudyId: guidOrNull(studyScheduleId),
    TrialStudyId: guidOrNull(trialStudyId || studyScheduleId),
    VisitId: guidOrNull(visitId),
    VisitName: visitName || "",
    ActiveTemplateType: activeTemplateType || "",
    IsTemplateUpdate: !!isTemplateUpdate,
    BeforeTemplate: mapBeforeTemplateForAudit(beforeTemplate, activeTemplateType),
    OrganizationId: organizationId,
    CreatedBy: createdBy,
    ActionType: "InsertTemplate",
    GroupId: guidOrNull(groupId),
    TemplateList: filled.map((t) => ({
      ...t,
      IsSMS: !!t.IsSMS,
      IsEmail: !!t.IsEmail,
      IsWhatsApp: !!t.IsWhatsApp,
      IsAdhoc: !!t.IsAdhoc,
    })),
  });
  if (!utils.isApiSuccess(response)) {
    throw new Error(
      utils.getApiResponseMessage(response, "Could not save communication setup."),
    );
  }
  await onAuditWritten?.();
  return response;
}

export async function saveVisitPayment({
  studyScheduleId,
  visitId,
  organizationId,
  createdBy,
  payment,
}) {
  const amount = Number(payment);
  if (!Number.isFinite(amount) || amount <= 0) {
    throw new Error("Payment value should be greater than zero.");
  }
  const response = await api.post(API_ENDPOINTS.ESCHEDULE.SAVE_ESCHEDULE, {
    StudyId: studyScheduleId,
    VisitId: visitId,
    OrganizationId: organizationId,
    CreatedBy: createdBy,
    Action: "SavePayment",
    Payment: String(payment),
  });
  if (!utils.isApiSuccess(response)) {
    throw new Error(
      getVisitApiErrorMessage(response, "Could not save visit payment."),
    );
  }
  return response;
}

export async function saveVisitCost({
  studyScheduleId,
  visitId,
  organizationId,
  createdBy,
  visitCost,
  randomCost = "",
}) {
  const cost = Number(visitCost);
  if (!Number.isFinite(cost) || cost <= 0) {
    throw new Error("Cost value should be greater than zero.");
  }
  if (randomCost !== "" && randomCost != null) {
    const rand = Number(randomCost);
    if (!Number.isFinite(rand) || rand <= 0) {
      throw new Error("Randomisation cost should be greater than zero.");
    }
  }
  const payload = {
    StudyId: studyScheduleId,
    VisitId: visitId,
    OrganizationId: organizationId,
    CreatedBy: createdBy,
    Action: "SaveCost",
    VisitCost: String(visitCost),
  };
  const randomCostValue = optionalDecimalField(randomCost);
  if (randomCostValue !== undefined) {
    payload.RandomCost = randomCostValue;
  }
  const response = await api.post(API_ENDPOINTS.ESCHEDULE.SAVE_ESCHEDULE, payload);
  if (!utils.isApiSuccess(response)) {
    throw new Error(
      getVisitApiErrorMessage(response, "Could not save visit cost."),
    );
  }
  return response;
}

export async function fetchStudyWorksheetOlderVersions({
  studyScheduleId,
  visitId,
  groupId,
  organizationId,
  createdBy,
  studyWorkSheetGroupId,
}) {
  if (!studyWorkSheetGroupId) return [];
  const response = await api.post(API_ENDPOINTS.ESCHEDULE.STUDY_WORKSHEET, {
    StudyWorkSheetId: studyWorkSheetGroupId,
    StudyIdRef: studyScheduleId,
    VisitIdRef: guidOrNull(visitId),
    GroupIdRef: guidOrNull(groupId),
    OrganizationId: organizationId,
    CreatedBy: createdBy,
    ActionType: "GetAllStudyWorkSheetOlderVersion",
  });
  if (!utils.isApiSuccess(response)) {
    return [];
  }
  const rows = extractSuccessStory(response);
  if (rows.length) return rows;
  return rowsFromResponse(response, [
    "SuccessStory",
    "successStory",
    "StudyWorkSheet",
    "studyWorkSheet",
    "Table",
    "Table1",
  ]);
}

export async function fetchStudyWorksheets({
  studyScheduleId,
  visitId,
  groupId,
  organizationId,
  createdBy,
}) {
  const response = await api.post(API_ENDPOINTS.ESCHEDULE.STUDY_WORKSHEET, {
    StudyIdRef: studyScheduleId,
    VisitIdRef: guidOrNull(visitId),
    GroupIdRef: guidOrNull(groupId),
    OrganizationId: organizationId,
    CreatedBy: createdBy,
    ActionType: "GetAllStudyWorkSheet",
  });
  if (!utils.isApiSuccess(response)) {
    return [];
  }
  const rows = extractSuccessStory(response);
  if (rows.length) return rows;
  return rowsFromResponse(response, [
    "SuccessStory",
    "successStory",
    "StudyWorkSheet",
    "studyWorkSheet",
    "Table",
    "Table1",
  ]);
}

export async function saveStudyWorksheet({
  studyScheduleId,
  visitId,
  groupId,
  organizationId,
  createdBy,
  workSheetName,
  workSheetUrl,
  file = null,
  documentName = "",
  fileExtension = "",
  fileBase64 = "",
  studyWorkSheetId = null,
  isSameForAllGroup = false,
}) {
  if (!String(workSheetName || "").trim()) {
    throw new Error("Enter worksheet name.");
  }

  let uploadBase64 = String(fileBase64 || "").trim();
  let resolvedDocumentName = String(documentName || "").trim();
  let resolvedExtension = String(fileExtension || "").trim().replace(/^\./, "");

  if (file) {
    uploadBase64 = await readWorksheetFileAsBase64(file);
    resolvedDocumentName = resolvedDocumentName || file.name;
    if (!resolvedExtension) {
      const lastDot = file.name.lastIndexOf(".");
      resolvedExtension =
        lastDot > 0 ? file.name.slice(lastDot + 1).toLowerCase() : "";
    }
  }

  if (!uploadBase64 && !String(workSheetUrl || "").trim()) {
    throw new Error("Choose a file to upload.");
  }

  const response = await api.post(API_ENDPOINTS.ESCHEDULE.STUDY_WORKSHEET, {
    StudyWorkSheetId: guidOrNull(studyWorkSheetId),
    StudyIdRef: studyScheduleId,
    VisitIdRef: guidOrNull(visitId),
    GroupIdRef: guidOrNull(groupId),
    WorkSheetName: workSheetName,
    WorkSheetURL: uploadBase64 || workSheetUrl,
    DocumentName: resolvedDocumentName || workSheetName,
    FileExtension: resolvedExtension,
    OrganizationId: organizationId,
    CreatedBy: createdBy,
    ActionType: "SaveSheet",
    IsSameForAllGroup: !!isSameForAllGroup,
  });
  if (!utils.isApiSuccess(response)) {
    throw new Error(
      utils.getApiResponseMessage(response, "Could not save worksheet."),
    );
  }
  const saveRow = firstSuccessStoryRow(response);
  if (isProcFailure(saveRow)) {
    throw new Error(
      saveRow?.MSG ||
        saveRow?.Msg ||
        utils.getApiResponseMessage(response, "Could not save worksheet."),
    );
  }
  return response;
}

export async function deleteStudyWorksheet({
  studyWorkSheetId,
  organizationId,
  createdBy,
}) {
  if (!studyWorkSheetId) {
    throw new Error("Document id is missing.");
  }
  const response = await api.post(API_ENDPOINTS.ESCHEDULE.STUDY_WORKSHEET, {
    StudyWorkSheetId: guidOrNull(studyWorkSheetId),
    OrganizationId: organizationId,
    CreatedBy: createdBy,
    ActionType: "DeleteSheet",
  });
  if (!utils.isApiSuccess(response)) {
    throw new Error(
      utils.getApiResponseMessage(response, "Could not remove document."),
    );
  }
  const saveRow = firstSuccessStoryRow(response);
  if (isProcFailure(saveRow)) {
    throw new Error(
      saveRow?.MSG ||
        saveRow?.Msg ||
        utils.getApiResponseMessage(response, "Could not remove document."),
    );
  }
  return response;
}

function readWorksheetFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const base64 = result.includes(",") ? result.split(",").pop() : result;
      resolve(base64);
    };
    reader.onerror = () => reject(new Error("Could not read the selected file."));
    reader.readAsDataURL(file);
  });
}

export async function saveDepartmentRole({
  studyScheduleId,
  visitId,
  organizationId,
  createdBy,
  departmentId,
  roleId,
  staffRequired,
  timeReq,
  groupId = null,
  actionType = "",
}) {
  const response = await api.post(API_ENDPOINTS.ESCHEDULE.DEPARTMENT_ROLE, {
    StudyId: studyScheduleId,
    VisitId: visitId,
    DepartmentId: departmentId,
    RoleId: roleId,
    StaffRequired: Number(staffRequired) || 0,
    TimeReq: Number(timeReq) || 0,
    OrganizationId: organizationId,
    CreatedBy: createdBy,
    ActionType: actionType,
    GroupId: guidOrNull(groupId),
  });
  if (!utils.isApiSuccess(response)) {
    throw new Error(
      utils.getApiResponseMessage(response, "Could not save department role."),
    );
  }
  return response;
}

export async function fetchAdhocSetup({
  studyScheduleId,
  organizationId,
  createdBy,
  groupId = null,
}) {
  const response = await api.post(API_ENDPOINTS.ESCHEDULE.SAVE_ECOMM_SETUP, {
    StudyId: studyScheduleId,
    VisitId: null,
    OrganizationId: organizationId,
    CreatedBy: createdBy,
    ActionType: "GetAdhocTemplate",
    GroupId: guidOrNull(groupId),
  });
  if (!utils.isApiSuccess(response)) {
    throw new Error(
      utils.getApiResponseMessage(response, "Could not load adhoc visit setup."),
    );
  }
  const templates = rowsFromResponse(response, ["TemplateList", "templateList"]);
  const list = templates.length
    ? templates
    : rowsFromUnknownTables(datasetObject(response));
  const whatsAppRows = rowsFromResponse(response, [
    "WhatsAppTemplates",
    "whatsAppTemplates",
  ]);
  const paymentRows = rowsFromResponse(response, [
    "Table1",
    "Table2",
    "PaymentDetails",
    "paymentDetails",
  ]);
  const paymentRow =
    paymentRows.find((row) => row?.Payment != null || row?.payment != null) ||
    paymentRows[0] ||
    {};
  const departments = rowsFromResponse(response, [
    "DepartmentRoleList",
    "departmentRoleList",
    "Table3",
    "Table4",
  ]);
  const currency = rowsFromResponse(response, [
    "CurrencySymbol",
    "currencySymbol",
    "Table5",
  ])[0];
  return {
    templates: mergeCommTemplates(list, whatsAppRows),
    payment: paymentRow?.Payment ?? paymentRow?.payment ?? "",
    visitCost: paymentRow?.VisitCost ?? paymentRow?.visitCost ?? "",
    departments,
    currencySymbol: currency?.CurrencySymbol || "£",
  };
}

export async function saveAdhocPayment({
  studyScheduleId,
  organizationId,
  createdBy,
  payment,
  groupId = null,
}) {
  const amount = Number(payment);
  if (!Number.isFinite(amount) || amount <= 0) {
    throw new Error("Payment value should be greater than zero.");
  }
  const response = await api.post(API_ENDPOINTS.ESCHEDULE.SAVE_ECOMM_SETUP, {
    StudyId: studyScheduleId,
    ActionType: "SaveAdhocpayment",
    Payment: String(payment),
    GroupId: groupId || null,
    OrganizationId: organizationId,
    CreatedBy: createdBy,
  });
  if (!utils.isApiSuccess(response)) {
    throw new Error(
      utils.getApiResponseMessage(response, "Could not save adhoc payment."),
    );
  }
  return response;
}

export async function saveAdhocCost({
  studyScheduleId,
  organizationId,
  createdBy,
  visitCost,
  groupId = null,
}) {
  const cost = Number(visitCost);
  if (!Number.isFinite(cost) || cost <= 0) {
    throw new Error("Cost value should be greater than zero.");
  }
  const response = await api.post(API_ENDPOINTS.ESCHEDULE.SAVE_ECOMM_SETUP, {
    StudyId: studyScheduleId,
    ActionType: "SaveAdhocCost",
    Payment: String(visitCost),
    GroupId: groupId || null,
    OrganizationId: organizationId,
    CreatedBy: createdBy,
  });
  if (!utils.isApiSuccess(response)) {
    throw new Error(
      utils.getApiResponseMessage(response, "Could not save adhoc cost."),
    );
  }
  return response;
}

export async function saveAdhocEcommTemplates({
  studyScheduleId,
  trialStudyId = null,
  organizationId,
  createdBy,
  templates,
  activeTemplateType,
  groupId = null,
  visitName = "Adhoc Visit",
  beforeTemplate = null,
  isTemplateUpdate = false,
  onAuditWritten = null,
  orgSmsEnabled = true,
  orgWhatsAppEnabled = true,
}) {
  const filled = validateCommTemplatesForSave({
    templates,
    activeTemplateType,
    orgSmsEnabled,
    orgWhatsAppEnabled,
  });
  const response = await api.post(API_ENDPOINTS.ESCHEDULE.SAVE_ECOMM_SETUP, {
    StudyId: guidOrNull(studyScheduleId),
    TrialStudyId: guidOrNull(trialStudyId || studyScheduleId),
    VisitId: null,
    VisitName: visitName || "Adhoc Visit",
    ActiveTemplateType: activeTemplateType || "",
    IsTemplateUpdate: !!isTemplateUpdate,
    BeforeTemplate: mapBeforeTemplateForAudit(beforeTemplate, activeTemplateType),
    OrganizationId: organizationId,
    CreatedBy: createdBy,
    ActionType: "InsertAdhocTemplate",
    GroupId: guidOrNull(groupId),
    TemplateList: filled.map((t) => ({
      ...t,
      IsSMS: !!t.IsSMS,
      IsEmail: !!t.IsEmail,
      IsWhatsApp: !!t.IsWhatsApp,
      IsAdhoc: !!t.IsAdhoc,
    })),
  });
  if (!utils.isApiSuccess(response)) {
    throw new Error(
      utils.getApiResponseMessage(response, "Could not save adhoc communication setup."),
    );
  }
  await onAuditWritten?.();
  return response;
}

export async function fetchAdhocWorksheets(ctx) {
  return fetchStudyWorksheets({
    ...ctx,
    visitId: null,
  });
}

export async function saveAdhocWorksheet(ctx) {
  return saveStudyWorksheet({
    ...ctx,
    visitId: null,
  });
}
